import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button, Form, Container, Image, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const resolveInventoryDays = (periodId, periods) =>
  (periods.find(p => p.id === periodId)?.inventoryDays ?? 0);

/**
 * Upsert product-inventory record:
 * 1) GET /product-inventory/product/{productUuid}
 *    - if exists → PATCH /product-inventory/product/{productUuid}
 *    - if 404   → POST  /product-inventory
 */
async function upsertProductInventory({
  productUuid,
  includeInStockTake,
  selectedInventoryPeriod,
  inventoryPeriods,
  uomInventoryId,
}) {
  const inventoryDaysValue = includeInStockTake
    ? resolveInventoryDays(selectedInventoryPeriod, inventoryPeriods)
    : 0;

  const patchPayload = {
    isProductStock: !!includeInStockTake,
    uomInventoryId: uomInventoryId || null,
    inventoryPeriod: inventoryDaysValue,
  };

  // If enabling stock take, a UOM inventory id must be present
  if (patchPayload.isProductStock && !patchPayload.uomInventoryId) {
    throw new Error('UOM Inventory is required when “Include in Stock Take” is enabled.');
  }

  try {
    await axios.get(`/product-inventory/product/${productUuid}`, { withCredentials: true });
    await axios.patch(
      `/product-inventory/product/${productUuid}`,
      patchPayload,
      { withCredentials: true }
    );
    return { action: 'patched' };
  } catch (err) {
    if (err?.response?.status === 404) {
      await axios.post(
        '/product-inventory',
        { productUuid, ...patchPayload },
        { withCredentials: true }
      );
      return { action: 'created' };
    }
    throw err;
  }
}

const ProductDetail = ({ selectedProduct, setActiveView }) => {
  const [product, setProduct] = useState(null);
  const [editableProduct, setEditableProduct] = useState({ metadata: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [uoms, setUoms] = useState([]);
  const [containerTypes, setContainerTypes] = useState([]);
  const [selectedContainerType, setSelectedContainerType] = useState('');
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [uomInventoryId, setUomInventoryId] = useState(null);

  const [includeInStockTake, setIncludeInStockTake] = useState(false);
  const [inventoryPeriods, setInventoryPeriods] = useState([]);
  const [selectedInventoryPeriod, setSelectedInventoryPeriod] = useState('');

  const { isAuthenticated, verifySession, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Resolve product ID from prop -> route state -> URL
  const stateProductId = location.state?.productId;
  const routeProductId = params.productId || params.id;
  const effectiveProductId = selectedProduct || stateProductId || routeProductId || '';

  // Helper: fetch container types for a specific UOM and update state
  const fetchContainerTypesForUom = async (uomId, cancelledRef) => {
    if (!uomId) {
      setContainerTypes([]);
      setSelectedContainerType('');
      return;
    }
    try {
      const res = await axios.get(`/container-types/uom/${uomId}`, { withCredentials: true });
      if (cancelledRef?.current) return;
      const list = res.data?.data || res.data || [];
      setContainerTypes(Array.isArray(list) ? list : []);
      setSelectedContainerType(prev => (list.some(ct => ct.id === prev) ? prev : ''));
    } catch (e) {
      console.warn('Failed to fetch container types for UOM:', e);
      if (!cancelledRef?.current) {
        setContainerTypes([]);
        setSelectedContainerType('');
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    const cancelledRef = { current: false };

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError('');
        await verifySession();

        if (!effectiveProductId) {
          if (!cancelled) {
            setError('No product selected. Please navigate from the products list.');
            setLoading(false);
          }
          return;
        }

        // --- Product ---
        const productRes = await axios.post(
          '/products/by-id',
          { id: effectiveProductId },
          { withCredentials: true }
        );
        if (cancelled) return;
        const productData = productRes.data;
        setProduct(productData);
        setEditableProduct(productData);

        // --- UOMs ---
        const allUomsRes = await axios.get('/uoms', { withCredentials: true });
        if (cancelled) return;
        setUoms(allUomsRes.data?.data || allUomsRes.data || []);

        // --- UOM-inventory + container logic ---
        try {
          const uomInvRes = await axios.get(
            `/uom-inventory/by-product/${effectiveProductId}`,
            { withCredentials: true }
          );
          if (!cancelled) {
            if (uomInvRes.data?.success && Array.isArray(uomInvRes.data.data) && uomInvRes.data.data.length > 0) {
              const uomInv = uomInvRes.data.data[0];
              setUomInventoryId(uomInv.id);

              try {
                const uomDetailRes = await axios.get(`/uoms/${uomInv.uomId}`, { withCredentials: true });
                if (!cancelled) {
                  setEditableProduct(prev => ({ ...prev, uom: uomDetailRes.data?.data }));
                }
              } catch { /* ignore */ }

              // Always fetch container types for the UOM (including 'each')
              await fetchContainerTypesForUom(uomInv.uomId, cancelledRef);

              // Preselect saved container type if any
              setSelectedContainerType(uomInv.containerTypeId || '');

              if (typeof uomInv.maxQuantity === 'number') setMaxQuantity(uomInv.maxQuantity);
            } else {
              // No UOM inventory yet → container list stays empty until UOM is chosen
              setContainerTypes([]);
              setSelectedContainerType('');
            }
          }
        } catch {
          setContainerTypes([]);
          setSelectedContainerType('');
        }

        // --- Inventory periods ---
        let periods = [];
        try {
          const ipRes = await axios.get('/inventory-periods', { withCredentials: true });
          if (!cancelled && ipRes.data?.success && Array.isArray(ipRes.data.data)) {
            periods = ipRes.data.data;
            setInventoryPeriods(periods);
          }
        } catch (err) {
          console.warn('Failed to fetch inventory periods:', err);
        }

        // --- Product-inventory (prefill switches) ---
        try {
          const piRes = await axios.get(
            `/product-inventory/product/${effectiveProductId}`,
            { withCredentials: true }
          );
          if (!cancelled && piRes.data?.success && piRes.data?.data) {
            const inv = piRes.data.data;
            setIncludeInStockTake(!!inv.isProductStock);
            const match = periods.find(p => p.inventoryDays === inv.inventoryPeriod);
            setSelectedInventoryPeriod(match?.id || '');
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            console.warn('Failed to fetch product inventory:', err);
          }
        }
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) logout();
        else if (!cancelled) setError(err.response?.data?.message || 'Failed to load product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAll();
    }

    return () => { cancelled = true; };
  }, [isAuthenticated, effectiveProductId, verifySession, logout]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'uom') {
      // Map back to the selected UOM object from "label - description"
      const sel = uoms.find(u => `${u.label} - ${u.description}` === value);
      setEditableProduct(prev => ({ ...prev, uom: sel || {} }));

      if (!sel) {
        setContainerTypes([]);
        setSelectedContainerType('');
        return;
      }

      // Always fetch container types for selected UOM (including 'each')
      await fetchContainerTypesForUom(sel.id);
      setSelectedContainerType(''); // force fresh choice

      // For 'each', zero-out max quantity (we hide it in UI)
      const isEach = (sel.label || '').toLowerCase().startsWith('each');
      if (isEach) setMaxQuantity(0);
    } else {
      setEditableProduct(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleMetadataChange = (key, value) => {
    setEditableProduct(prev => ({
      ...prev,
      metadata: { ...prev.metadata, [key]: value }
    }));
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await verifySession();

      // 1) Update core product fields
      await axios.patch(
        `/products/${product.id}`,
        {
          name: editableProduct.name,
          description: editableProduct.description,
          imageURL: editableProduct.imageURL,
          category: editableProduct.category,
          metadata: editableProduct.metadata
        },
        { withCredentials: true }
      );

      // 2) Upsert UOM-inventory
      const isEach = (editableProduct.uom?.label || '').toLowerCase().startsWith('each');
      const uomPayload = isEach
        ? {
            productUuid: effectiveProductId,
            uomId: editableProduct.uom?.id,
            // Now also persist container type for 'each'
            containerTypeId: selectedContainerType || undefined,
          }
        : {
            productUuid: effectiveProductId,
            uomId: editableProduct.uom?.id,
            maxQuantity: Number(maxQuantity),
            containerTypeId: selectedContainerType || undefined,
          };

      let newUomInventoryId = uomInventoryId;
      if (uomInventoryId) {
        await axios.put(`/uom-inventory/${uomInventoryId}`, uomPayload, { withCredentials: true });
      } else {
        const uomInvRes = await axios.post(`/uom-inventory`, uomPayload, { withCredentials: true });
        newUomInventoryId = uomInvRes.data?.data?.id;
        setUomInventoryId(newUomInventoryId);
      }

      // 3) Upsert Product-Inventory (GET → PATCH | 404 → POST)
      await upsertProductInventory({
        productUuid: effectiveProductId,
        includeInStockTake,
        selectedInventoryPeriod,
        inventoryPeriods,
        uomInventoryId: newUomInventoryId,
      });

      setSuccess('Product and inventory updated successfully!');
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) logout();
      else setError(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (typeof setActiveView === 'function') setActiveView('products');
    navigate('/dashboard/products');
  };

  if (!isAuthenticated) return null;

  if (loading && !product) {
    return (
      <div className="content-container text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="content-container text-center py-5">
        <Alert variant="danger">{error || 'Product not found'}</Alert>
        <Button onClick={handleBackClick}>Back</Button>
      </div>
    );
  }

  const isEachUom = (editableProduct.uom?.label || '').toLowerCase().startsWith('each');
  const uomSelected = !!editableProduct.uom?.id;

  return (
    <div className="content-container">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-secondary" onClick={handleBackClick} disabled={loading}>
          ← Back to Products
        </Button>
        <h1 className="mx-auto">{product.name}</h1>
        <Button variant="primary" onClick={handleUpdate} disabled={loading}>
          {loading
            ? <><Spinner as="span" size="sm" animation="border" />{' '}<span className="ms-2">Updating...</span></>
            : 'Update Product'}
        </Button>
      </div>

      <Container className="text-center my-4">
        <Image
          src={product.imageURL || '/placeholder-product.png'}
          alt={product.name}
          style={{ maxHeight: '300px' }}
          className="img-fluid"
        />
      </Container>

      <Form className="mt-4">
        {/* Product fields */}
        <Form.Group className="mb-3">
          <Form.Label>Product Name</Form.Label>
          <Form.Control
            name="name"
            value={editableProduct.name ?? ''}
            onChange={handleInputChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            rows={3}
            value={editableProduct.description || ''}
            onChange={handleInputChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Image URL</Form.Label>
          <Form.Control
            name="imageURL"
            value={editableProduct.imageURL || ''}
            onChange={handleInputChange}
            disabled={loading}
          />
        </Form.Group>

        {/* UOM selector */}
        <Form.Group className="mb-3">
          <Form.Label>Unit of Measure</Form.Label>
          <Form.Select
            name="uom"
            value={
              editableProduct.uom?.label
                ? `${editableProduct.uom?.label} - ${editableProduct.uom?.description || ''}`
                : ''
            }
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="">Select UOM</option>
            {uoms.map(u => (
              <option key={u.id} value={`${u.label} - ${u.description}`}>
                {u.label} - {u.description}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Container Type: now shown whenever a UOM is selected, including 'each' */}
        {uomSelected && (
          <Form.Group className="mb-3">
            <Form.Label>Container Type</Form.Label>
            <Form.Select
              value={selectedContainerType}
              onChange={e => setSelectedContainerType(e.target.value)}
              disabled={loading || containerTypes.length === 0}
            >
              <option value="">
                {containerTypes.length ? 'Select Container Type' : 'No container types for selected UOM'}
              </option>
              {containerTypes.map(c => (
                <option key={c.id} value={c.id}>
                  {c.friendlyName || c.containerTypeName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Max Quantity: hidden for 'each' */}
        {uomSelected && !isEachUom && (
          <Form.Group className="mb-3">
            <Form.Label>Max Quantity</Form.Label>
            <Form.Control
              type="number"
              value={maxQuantity}
              onChange={e => setMaxQuantity(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        )}

        {/* Include in Stock Take */}
        <Form.Group className="mb-3">
          <div className="d-flex align-items-center">
            <Form.Label htmlFor="include-in-stock" className="mb-0 me-2">
              Include in Stock Take
            </Form.Label>
            <Form.Check
              type="checkbox"
              id="include-in-stock"
              checked={includeInStockTake}
              onChange={e => setIncludeInStockTake(e.target.checked)}
              disabled={loading}
              className="mb-0"
              label=""
            />
          </div>
        </Form.Group>

        {/* Stock Take Frequency */}
        {includeInStockTake && (
          <Form.Group className="mb-3">
            <Form.Label>Stock Take Frequency</Form.Label>
            <Form.Select
              value={selectedInventoryPeriod}
              onChange={e => setSelectedInventoryPeriod(e.target.value)}
              disabled={loading}
            >
              <option value="">Select Frequency</option>
              {inventoryPeriods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.inventoryPeriodDesc || period.inventoryPeriodName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Metadata */}
        {product.metadata &&
          Object.entries(product.metadata).map(([key]) => (
            <Form.Group key={key} className="mb-3">
              <Form.Label>{key}</Form.Label>
              <Form.Control
                value={editableProduct.metadata[key] ?? ''}
                onChange={e => handleMetadataChange(key, e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          ))}

        {/* Category */}
        <Form.Group className="mb-3">
          <Form.Label>Category ID</Form.Label>
          <Form.Control value={product.category} readOnly plaintext />
        </Form.Group>
      </Form>
    </div>
  );
};

export default ProductDetail;